/**
 * ManeuverView component - Display collision avoidance maneuver plans
 *
 * Features:
 * - Uses ManeuverSummaryCard for retro mission control styled data display
 * - Uses ManeuverVisualization for interactive mission_control.html iframe
 * - Handles both new VNB-frame format and legacy formats
 * - Clean, professional layout with retro 80s theme
 */

import { Rocket } from 'lucide-react'
import type { Artifact, ManeuverArtifactData } from '../../types/chat'
import ManeuverSummaryCard from './ManeuverSummaryCard'
import ManeuverVisualization from './ManeuverVisualization'

interface ManeuverViewProps {
  artifacts: Artifact[]
}

/**
 * Type guard to check if artifact data is ManeuverArtifactData (new format)
 */
function isManeuverArtifactData(data: unknown): data is ManeuverArtifactData {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    typeof d.delta_v === 'object' &&
    d.delta_v !== null &&
    typeof d.maneuver_time === 'string' &&
    typeof d.predicted_miss_distance_km === 'number' &&
    typeof d.tca_shift_seconds === 'number' &&
    typeof d.primary_norad_id === 'number' &&
    typeof d.secondary_norad_id === 'number'
  )
}

/**
 * Legacy maneuver data format (for backward compatibility)
 */
interface LegacyManeuverData {
  cdm_id?: string
  primary_object?: string
  secondary_object?: string
  tca?: string
  original_pc?: number
  final_pc?: number
  delta_v?: {
    magnitude?: number
    radial?: number
    in_track?: number
    cross_track?: number
    unit?: string
  }
  maneuver_time?: string
  warning_time_hours?: number
  fuel_cost_kg?: number
  risk_reduction?: number
  status?: string
  notes?: string
}

/**
 * Render a single maneuver with new format using ManeuverSummaryCard and ManeuverVisualization
 */
function NewFormatManeuverCard({ data, title }: { data: ManeuverArtifactData; title: string }) {
  const missDistanceBeforeM =
    data.original_miss_distance_km != null ? data.original_miss_distance_km * 1000 : null

  return (
    <div className="space-y-4">
      {/* Title */}
      <div className="flex items-center gap-3 pb-2 border-b border-dark-200">
        <Rocket className="w-5 h-5 text-retro-text" />
        <h3 className="font-mono text-retro-text font-medium">
          {title || 'Collision Avoidance Maneuver'}
        </h3>
      </div>

      {/* Maneuver Summary Card */}
      <ManeuverSummaryCard
        deltaV={data.delta_v}
        maneuverTime={data.maneuver_time}
        missDistanceBefore={missDistanceBeforeM}
        missDistanceAfter={data.predicted_miss_distance_km * 1000} // Convert km to m for display
        tcaShift={data.tca_shift_seconds}
        primaryNoradId={data.primary_norad_id}
        secondaryNoradId={data.secondary_norad_id}
      />

      {/* Mission Control Visualization (single artifact per ADR-011) */}
      {data.mission_control_path && (
        <div className="mt-4">
          <ManeuverVisualization
            plotUrl={data.mission_control_path}
            title="MISSION CONTROL"
          />
        </div>
      )}
    </div>
  )
}

/**
 * Render legacy format maneuver data (backward compatibility)
 */
function LegacyManeuverCard({ data, title }: { data: LegacyManeuverData; title: string }) {
  return (
    <div className="border-2 border-retro-border rounded-lg overflow-hidden bg-retro-bg">
      {/* Header */}
      <div className="bg-retro-hover px-4 py-3 border-b border-retro-border flex items-center gap-3">
        <Rocket className="w-5 h-5 text-retro-text" />
        <h3 className="font-mono text-retro-text font-medium">
          {title || 'Maneuver Plan'}
        </h3>
        {data.status && (
          <span className={`
            ml-auto px-2 py-0.5 rounded text-xs font-mono
            ${data.status === 'approved' ? 'bg-green-500/20 text-green-400' :
              data.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-dark-200 text-dark-500'}
          `}>
            {data.status.toUpperCase()}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Objects involved */}
        {(data.primary_object || data.secondary_object) && (
          <div className="grid grid-cols-2 gap-4 pb-4 border-b border-dark-200">
            <div>
              <label className="text-xs text-dark-500 uppercase tracking-wider">
                Primary Object
              </label>
              <p className="font-mono text-sm text-retro-text">
                {data.primary_object || '-'}
              </p>
            </div>
            <div>
              <label className="text-xs text-dark-500 uppercase tracking-wider">
                Secondary Object
              </label>
              <p className="font-mono text-sm text-dark-600">
                {data.secondary_object || '-'}
              </p>
            </div>
          </div>
        )}

        {/* Delta-V breakdown */}
        {data.delta_v && (
          <div className="pb-4 border-b border-dark-200">
            <div className="flex items-center gap-2 mb-2">
              <Rocket className="w-4 h-4 text-retro-text" />
              <label className="text-xs text-dark-500 uppercase tracking-wider">
                Delta-V Components
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {data.delta_v.magnitude !== undefined && (
                <div className="bg-dark-50 rounded p-2">
                  <label className="text-xs text-dark-500">Magnitude</label>
                  <p className="font-mono text-sm text-retro-text font-bold">
                    {data.delta_v.magnitude.toFixed(4)} {data.delta_v.unit || 'm/s'}
                  </p>
                </div>
              )}
              {data.delta_v.radial !== undefined && (
                <div className="bg-dark-50 rounded p-2">
                  <label className="text-xs text-dark-500">Radial</label>
                  <p className="font-mono text-sm text-dark-600">
                    {data.delta_v.radial.toFixed(4)}
                  </p>
                </div>
              )}
              {data.delta_v.in_track !== undefined && (
                <div className="bg-dark-50 rounded p-2">
                  <label className="text-xs text-dark-500">In-Track</label>
                  <p className="font-mono text-sm text-dark-600">
                    {data.delta_v.in_track.toFixed(4)}
                  </p>
                </div>
              )}
              {data.delta_v.cross_track !== undefined && (
                <div className="bg-dark-50 rounded p-2">
                  <label className="text-xs text-dark-500">Cross-Track</label>
                  <p className="font-mono text-sm text-dark-600">
                    {data.delta_v.cross_track.toFixed(4)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timing */}
        {data.maneuver_time && (
          <div className="pb-4 border-b border-dark-200">
            <label className="text-xs text-dark-500 uppercase tracking-wider">
              Maneuver Time
            </label>
            <p className="font-mono text-sm text-retro-text">
              {new Date(data.maneuver_time).toLocaleString()}
            </p>
          </div>
        )}

        {/* Notes */}
        {data.notes && (
          <div>
            <label className="text-xs text-dark-500 uppercase tracking-wider">
              Notes
            </label>
            <p className="text-sm text-dark-600 mt-1">
              {data.notes}
            </p>
          </div>
        )}
      </div>

      {/* CDM Reference */}
      {data.cdm_id && (
        <div className="bg-dark-100 px-4 py-2 border-t border-dark-200">
          <span className="text-xs text-dark-500 font-mono">
            CDM: {data.cdm_id}
          </span>
        </div>
      )}
    </div>
  )
}

function ManeuverView({ artifacts }: ManeuverViewProps) {
  // Filter to only maneuver type artifacts
  // Use string comparison to handle both enum and raw string values from backend
  const maneuverArtifacts = artifacts.filter(a => String(a.type) === 'maneuver')

  if (maneuverArtifacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-dark-400">
        <div className="text-center">
          <Rocket className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm">No maneuver plans yet</p>
          <p className="text-xs mt-2 text-dark-500">
            Select a CDM and click "Plan Maneuver" to generate a collision avoidance plan
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {maneuverArtifacts.map((artifact, index) => {
        // Check if this is the new maneuver format
        if (artifact.data && isManeuverArtifactData(artifact.data)) {
          return (
            <NewFormatManeuverCard
              key={index}
              data={artifact.data}
              title={artifact.title}
            />
          )
        }

        // Fall back to legacy format
        const legacyData = artifact.data as LegacyManeuverData | null
        if (legacyData) {
          return (
            <LegacyManeuverCard
              key={index}
              data={legacyData}
              title={artifact.title}
            />
          )
        }

        // No data available
        return (
          <div
            key={index}
            className="border-2 border-retro-border rounded-lg overflow-hidden bg-retro-bg"
          >
            <div className="bg-retro-hover px-4 py-3 border-b border-retro-border flex items-center gap-3">
              <Rocket className="w-5 h-5 text-retro-text" />
              <h3 className="font-mono text-retro-text font-medium">
                {artifact.title || 'Maneuver Plan'}
              </h3>
            </div>
            <div className="p-4 text-center py-8 text-dark-400">
              <p className="text-sm italic">No maneuver data available</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ManeuverView
